<template>
  <div :class="['question-item', {'inactive': !element.active}, {'domain-open': domainOpen}]">
    <i v-if="element.subtype === 'domain'" class="sherpa-icon icon-lista item-icon"></i>
    <i v-else-if="element.subtype === 'text'" class="sherpa-icon icon-editar item-icon"></i>
    <i v-else class="sherpa-icon icon-preguntes item-icon"></i>
    <div class="question-description">
      <language-text
        :id="element.id"
        :names="element.name"
        :getName="getName"
        :getLang="getLang"
        :selected="selected"
        :currentLanguage="currentLanguage"
        :active="element.active"
        :formBuilderContainer="formBuilderContainer"
        :highlightTerm="highlightTerm"
        @languageTextPopupOpen="languageTextPopupOpen"
      />
      <span class="description id" v-html="highlightText(element.id)"></span>
      <span :class="['description', 'type', {'description-domain': activeDomain}]" @click.stop="domainToggle">
        <span v-html="getTypeName(element, selected)"></span>
        <template v-if="activeDomain">
          <transition name="fade-slide-right" mode="out-in">
            <spinner-loading v-if="loadingDomain" class="spinner-loading" :line-size='2' line-fg-color='#076cfabe' :size="12" />
            <i v-else class="fas fa-chevron-circle-down fa-sm"></i>
          </transition>
        </template>
      </span>
    </div>
    <template v-if="selected">
      <div class="edition-element">
        <toggle-button
          v-model="element.required"
          :sync="true"
          :labels="{checked: getLang('required'), unchecked: getLang('optional')}"
          :width="103"
          :font-size="11"
          :margin="3"
          :speed="200"
          :color="{checked: '#212c62c5', unchecked: '#899dffc5', disabled: '#cfcfcf'}"
          :disabled="!element.active"
          @change="requiredChange"
        />
        <toggle-button
          v-model="element.active"
          :sync="true"
          :labels="{checked: getLang('active'), unchecked: getLang('inactive')}"
          :width="80"
          :font-size="11"
          :margin="3"
          :speed="200"
          :color="{checked: '#4ab800c5', unchecked: '#a6a6a6', disabled: '#efefef'}"
          @change="activeChange"
        />
        <i class="sherpa-icon icon-trash" @click="deleteElement"></i>
        <div class="builder-select action">
          <multiselect
            :options="actionList"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            :disabled="!element.active"
            track-by="key"
            label="name"
            v-model="selectedAction"
          />
        </div>
      </div>
      <div v-if="activeDomain" :class="['domain-data',{'no-responses': !element.domain || (element.domain.notSelected.length + element.domain.selected.length) === 0}]">
        <div class="responsesNumber">
          <span class="label"><i class="fas fa-sliders-h"></i> {{ getLang('responsesNumber') }}</span>
          <div class="slider"
            @mousedown.stop
            @touchstart.stop
            @pointerdown.stop
            @dragstart.stop
            @click.stop
          >
            <vue-slider
              v-if="element.domain"
              :min="minResponsesOption"
              :max="maxResponsesOption"
              :lazy="true"
              :enable-cross="true"
              :tooltip="'always'"
              :tooltip-placement="['bottom', 'bottom']"
              v-model="element.domain.minMaxResponses"
              @change="onSliderChange"
            />
          </div>
        </div>
        <div
          v-if="element.domain && (element.domain.notSelected.length + element.domain.selected.length) > 0"
          class="responses"
        >
          <span class="label"><i class="fas fa-tasks"></i> {{ getLang('responseSelection') }}</span>
          <div class="responses-list"
            @mousedown.stop
            @touchstart.stop
            @pointerdown.stop
            @dragstart.stop
            @click.stop
          >
            <dual-list v-if="element.domain"
              :key="'dual-list_' + element.id"
              :element="element"
              :getName="getName"
              :getLang="getLang"
              :currentLanguage="currentLanguage"
              :selected="selected"
              :formBuilderContainer="formBuilderContainer"
              @languageTextPopupOpen="languageTextPopupOpen"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import languageText from './languageText'
import ToggleButton from '@/components/shared/ToggleButton.vue'
import Multiselect from '@/components/shared/Multiselect.vue' // 'vue-multiselect ^2.1.6' component with TMD modifications
import VueSlider from 'vue-slider-component'
import dualList from './dualList'
import Configuration from '@/configuration'
import * as api from '../definitionLoader/utils/definitionApi'
import moment from 'moment'

export default {
  name: 'question-item',
  components: {
    languageText,
    ToggleButton,
    Multiselect,
    VueSlider,
    dualList
  },
  props: {
    element: Object,
    parent: Object,
    getName: Function,
    getTypeName: Function,
    getLang: Function,
    selected: Boolean,
    currentLanguage: String,
    formBuilderContainer: HTMLElement,
    domainEndpoint: String,
    formId: String,
    deactivateSelectedListConditionals: Function,
    conditionalSelected: Array,
    highlightTerm: { type: String, default: '' }
  },
  data () {
    return {
      actionList: [
        {key: 'R', name: this.getLang('read')},
        {key: 'C', name: this.getLang('create')},
        {key: 'U', name: this.getLang('update')}
      ],
      domainOpen: false,
      minResponsesOption: 0,
      maxResponsesOption: this.getMaxOptionsValue(),
      loadingDomain: false
    }
  },
  methods: {
    highlightText (text) {
      if (!this.highlightTerm || !text) return text
      const norm = s => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      const normText = norm(text)
      const normTerm = norm(this.highlightTerm)
      if (!normTerm) return text
      let result = '', i = 0
      while (i < text.length) {
        if (normText.substring(i, i + normTerm.length) === normTerm) {
          result += '<mark>' + text.substring(i, i + normTerm.length) + '</mark>'
          i += normTerm.length
        } else { result += text[i]; i++ }
      }
      return result
    },
    getMaxOptionsValue () {
      return this.element.domain && (this.element.domain.selected.length + this.element.domain.notSelected.length) > 0 ? this.element.domain.selected.length + this.element.domain.notSelected.length : 99
    },
    deleteElement () {
      this.$emit('deleteElementCall', this.element)
    },
    languageTextPopupOpen (open) {
      this.$emit('languageTextPopupOpen', open)
    },
    activeChange (toggle) {
      if (toggle.value === true) {
        this.$emit('propagateActive', this.element)
      } else {
        this.deactivateSelectedListConditionals(this.element, this.parent ? this.parent.children : null, false, null)
        this.$emit('propagateInactive', this.element)
      }
    },
    domainToggle () {
      if (this.domainOpen) {
        this.domainOpen = false
      } else {
        if (this.element.domain) {
          this.domainOpen = true
        } else {
          this.loadDomain(true)
        }
      }
    },
    loadDomain (openDomain) {
      this.loadingDomain = true
      api.call({domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(), url: this.domainEndpoint, token: null, version: null, method: 'POST', lang: this.currentLanguage, authorization: window.localStorage.getItem('user-token'), data: { id: this.formId, code: this.element.id }})
        .then(resp => {
          if (resp) {
            this.element.domain = resp
            this.setMinResponsesOption()
            this.maxResponsesOption = this.getMaxOptionsValue()
            this.domainOpen = openDomain
            this.loadingDomain = false
          }
        })
        .catch(err => {
          console.log(err)
          this.loadingDomain = false
        })
    },
    requiredChange () {
      this.setMinResponsesOption()
    },
    setMinResponsesOption () {
      if (this.element.domain) {
        if (this.element.required) {
          if (this.element.domain.minMaxResponses[0] < 1) {
            this.element.domain.minMaxResponses = [1, this.element.domain.minMaxResponses[1]]
          }
        } else {
          if (this.element.domain.minMaxResponses[0] > 0) {
            this.element.domain.minMaxResponses = [0, this.element.domain.minMaxResponses[1]]
          }
        }
      }
    },
    onSliderChange (value) {
      if (!this.element.required && value[0] > 0) {
        this.element.required = true
      } else if (this.element.required && value[0] === 0) {
        this.element.required = false
      }
    }
  },
  beforeMount () {
    this.setMinResponsesOption()
  },
  computed: {
    selectedAction: {
      get () {
        return this.actionList.find(option => option.key === this.element.action) || null
      },
      set (selected) {
        this.element.action = selected ? selected.key : null
      }
    },
    activeDomain () {
      return this.selected && this.element.active && this.element.subtype === 'domain'
    }
  },
  watch: {
    conditionalSelected: {
      handler (newVal, oldVal) {
        if (newVal && newVal.includes(this.element.id) && !this.element.domain) {
          this.loadDomain(false)
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
mark {
  background-color: #fff176;
  padding: 0;
}

.form-builder .question-item {
  position: relative;
  transition: all 0.2s ease;
  padding: 12px 13px 8px;

  &.inactive {
    opacity: 0.6;
  }

  .description-domain {
    cursor: pointer;
    transition: all 0.2s ease;
    color: #076cfabe !important;
    border: 1px dotted transparent;
    border-radius: 2px;

    &:hover {
      background-color: #e4fffe;
      border: 1px dotted #999;

      i {
        color: #076cfabe;
      }
    }

    i {
      vertical-align: inherit !important;
      font-size: 12.5px;
      color: #076cfabe;
      margin-left: 2px;
      transition: all 0.2s ease;
    }

    .spinner-loading {
      display: inline-block;
      margin-left: 2px;
      padding-top: 2px;
      padding-right: 5px;
      vertical-align: text-top;
    }
  }

  .domain-data {
    display: block;
    min-height: 0px;
    height: 0px;
    padding: 0 7px 0 28px;
    overflow: hidden;
    opacity: 0;
    transition: all 0.2s ease;
    margin: 0 7px 0 21.5px;
    border-top-left-radius: 0;
    border-top-right-radius: 2px;
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;

    .slider {
      padding: 0 20px 0 0;
    }

    .label {
      font-size: 14px;
      color: #444;
      font-weight: 400;
      letter-spacing: 0;
      text-align: left;
    }

    .responses,
    .responsesNumber {
      .label {
        i {
          margin-right: 0;
          font-size: 13px;
          vertical-align: baseline;
          width: 15px;
        }
      }
    }

    .responses {
      margin-top: 40px;
    }
  }

  &.domain-open {
    .description-domain {
      color: #076cfabe;
      background-color: #e4fffe;
      border-top-left-radius: 2px;
      border-top-right-radius: 2px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      i {
        transform: scaleY(-1);
      }
    }

    .domain-data {
      min-height: 370px;
      height: 370px;
      padding: 10px 7px 10px 28px;
      overflow: initial;
      opacity: 1;
      border: 1px dotted #999;
      background-color: #e4fffe62;
      margin-bottom: 5px;
      margin-right: 4px;
      margin-top: 20px;

      &.no-responses {
        min-height: 100px;
        height: 100px;
      }
    }
  }

  .edition-element {
    right: 0;
    top: 0;
    bottom: 0;
    padding: 14px 15px 15px 18px;
    border-left: 1px dotted #999;
    border-bottom: 1px dotted #999;
    background-color: #f8f8f8;
    height: 92px;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
  }
}
</style>
